import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import * as context from 'Context';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {DataTable, DataTableColumn} from 'notebook/data-tables/DataTableConstants';
import {NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {ProxiedNotePropTypes} from 'notebook/util/NoteUtilConstants';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {exportRunResults} from 'results/assays/AssayRecordActions';
import {AssayResult, AssayRun, isOversized} from 'results/assays/AssayRecordConstants';
import {DisplayConfig} from 'runs/AttachmentConstants';
import withPublishedDataTableColumnsQuery from 'runs/withPublishedDataTableColumnsQuery';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {Optional} from 'util/Types';

import {recreateEmptyDataTablesForAssayGroup} from './AssayGroupDataTables';
// @ts-expect-error
import AssayGroupResultsTable from './AssayGroupResultsTable';

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class AssayResultsContainerConnector extends DataConnector<AssayResultsContainerProps, ConnectedProps> {
  override stores = [SchemaFieldSelectorStore];

  enhanceProps(): ConnectedProps {
    // eslint-disable-next-line no-restricted-syntax
    const schemaFields = _.flatten(this.props.assayResultSchemas.map((schema) => schema.fields));
    return {
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(schemaFields)
      ),
    };
  }
}

export interface AssayResultsContainerProps extends ProxiedNotePropTypes {
  assayResults: Array<AssayResult> | null;
  assayResultSchemas: Array<AssaySchema>;
  assayRun: AssayRun;
  childrenFetching: boolean;
  dataTables: Array<DataTable>;
  displayConfigBySchemaId: {[schemaId: string]: DisplayConfig};
  editor: object;
  editorProps: NoteEditorProps;
  provenanceUuid: string;
  handleUpdateNoteLinkedAssayData: ({dataTables}: {dataTables: Array<DataTable>}) => void;
  resultSchemaToCount: {[schemaId: string]: number};
  usedTableNames: () => Array<string>;
}

class AssayResultsContainer extends React.PureComponent<
  AssayResultsContainerProps &
    ConnectedProps & {
      publishedDataTableColumnsBySchema: {[schemaId: string]: Optional<Array<DataTableColumn>>};
    }
> {
  _updateDataTablesTimeoutId: Optional<number>;
  _cachedDataTables?: Array<DataTable>;

  override componentDidMount(): void {
    this._deferredUpdateDataTables();
  }

  override componentDidUpdate(): void {
    this._deferredUpdateDataTables();
  }

  override componentWillUnmount(): void {
    if (this._updateDataTablesTimeoutId != null) {
      clearTimeout(this._updateDataTablesTimeoutId);
    }
  }

  _deferredUpdateDataTables(): void {
    if (this.props.viewer) {
      return;
    }
    if (this._updateDataTablesTimeoutId != null) {
      clearTimeout(this._updateDataTablesTimeoutId);
    }
    this._updateDataTablesTimeoutId = window.setTimeout(() => {
      this._updateDataTables();
    }, 0);
  }

  _createDataTables(): Array<DataTable> {
    const newDataTables = recreateEmptyDataTablesForAssayGroup(
      this.props.assayResultSchemas,
      this.props.displayConfigBySchemaId,
      this._cachedDataTables ?? this.props.dataTables,
      this.props.usedTableNames(),
      this.props.selectorsById,
      this.props.publishedDataTableColumnsBySchema
    );
    if (isEqual(newDataTables, this.props.dataTables)) {
      return this.props.dataTables;
    }
    this.props.dataTables.forEach((dataTable) => {
      if (!newDataTables.some((newDataTable) => newDataTable.cid === dataTable.cid)) {
        assert(dataTable.rows.length === 0, 'Unsafe to remove table if there is data');
      }
    });
    return newDataTables;
  }

  /**
   * To be called when this component updates (to handle processing newly fetched data), we recompute the
   * necessary data tables for rendering, and save them onto the NoteLinkedObject.
   *
   * TODO(BNCH-2771): This can be done synchronously once results are manually associated with an assay group.
   */
  _updateDataTables(): void {
    const newDataTables = this._createDataTables();
    if (newDataTables === this.props.dataTables) {
      return;
    }
    context.using({skipBumpVersionForNonEdit: true}, this, () => {
      this.props.handleUpdateNoteLinkedAssayData({dataTables: newDataTables});
    });
  }

  /**
   * Get the data table that will be rendered by the component
   *
   * HACK: If this component is being loaded server-side, i.e. for the print view, render the new data tables
   * immediately. Server-side rendering has no lifecycles so the tables are not reflected with the normal,
   * browser deferred data table creations.
   */
  _getDataTables(): Array<DataTable> {
    this._cachedDataTables = !this.props.viewer ? this.props.dataTables : this._createDataTables();
    return this._cachedDataTables;
  }

  _handleDataTableUpdate = (newDataTable: DataTable): void => {
    assert(newDataTable.rows.length === 0, 'Expected empty data table');
    const newDataTables = this.props.dataTables.map((dataTable) =>
      dataTable.cid === newDataTable.cid ? newDataTable : dataTable
    );
    assert(newDataTables.includes(newDataTable), 'Updated data table unexpectedly not found in assay group');
    this.props.handleUpdateNoteLinkedAssayData({dataTables: newDataTables});
  };

  _downloadAsCSV = (resultSchemaId: string): void => {
    exportRunResults(this.props.assayRun.id, resultSchemaId);
  };

  override render(): JSX.Element {
    if (this.props.assayResults == null) {
      return (
        <div className="u-text-center">
          <HorizontalThrobber />
        </div>
      );
    }

    const resultsBySchemaId = _.groupBy(this.props.assayResults, 'schemaId');
    const dataTablesBySchemaId = _.indexBy(
      this._getDataTables(),
      (dataTable) => dataTable.config.assaySchema.id
    );
    const dataTablesExist = this.props.assayResultSchemas.every(
      // eslint-disable-next-line benchlint/no-in-operator
      (schema) => schema.id in dataTablesBySchemaId
    );
    return (
      <div>
        {dataTablesExist &&
          this.props.assayResultSchemas.map((schema) => (
            <div className="resultsAttachment-assayRun-tableWrapper" key={schema.id}>
              <AssayGroupResultsTable
                schema={schema}
                results={resultsBySchemaId[schema.id] || []}
                dataTable={dataTablesBySchemaId[schema.id]}
                downloadAsCSV={() => {
                  this._downloadAsCSV(schema.id);
                }}
                forceRowCount={
                  isOversized(this.props.resultSchemaToCount[schema.id])
                    ? this.props.resultSchemaToCount[schema.id]
                    : undefined
                }
                isOversized={isOversized(this.props.resultSchemaToCount[schema.id])}
                onDataTableUpdate={this._handleDataTableUpdate}
                editor={this.props.editor}
                editorProps={this.props.editorProps}
              />
            </div>
          ))}

        {(this.props.childrenFetching || !dataTablesExist) && (
          <div className="u-text-center">
            <HorizontalThrobber />
          </div>
        )}
      </div>
    );
  }
}

export default AssayResultsContainerConnector.connect(
  withPublishedDataTableColumnsQuery(AssayResultsContainer, (props) => props.assayResultSchemas)
);
